import {
  ArrowPathIcon,
  FunnelIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Input,
  SelectItem,
  Tooltip,
} from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import { connectStripe } from "../../services/api";
import StripeAccountDetailsModal from "../../components/stripe/stripeAccountDetailsModal";
import AppSelect from "../../components/common/appSelect";

interface MarketplaceCardsFiltersProps {
  profileType: string;
  activeTab: "all" | "myCards";
  searchName: string;
  setSearchName: (value: string) => void;
  filterCardType: string;
  setFilterCardType: (value: string) => void;
  filterLocation: string;
  setFilterLocation: (value: string) => void;
  filterSport: string;
  setFilterSport: (value: string) => void;
  filterWeightClass: string;
  setFilterWeightClass: (value: string) => void;
  filterPurse: string;
  setFilterPurse: (value: string) => void;
  filterCurrency: string;
  setFilterCurrency: (value: string) => void;
  filterOpportunityType: string;
  setFilterOpportunityType: (value: string) => void;
  selectedRosterStatus: string;
  setSelectedRosterStatus: (value: string) => void;
  selectedTitle: string;
  setSelectedTitle: (value: string) => void;
  locations: string[];
  sports: string[];
  weightClasses: string[];
  purses: string[];
  opportunityTypes: string[];
  rosterStatus: string[];
  handleResetFilters: () => void;
  setIsCreateRosterModalOpen: (value: boolean) => void;
  setIsOpportunityModalOpen: (value: boolean) => void;
  connectStatus: string | null;
  connectStatusLoading?: boolean;
  onCreateOpportunity: () => void;
  onTabChange: (key: string) => void;
}

const selectClassNames = {
  trigger:
    "h-9 min-h-9 rounded-xl bg-zinc-100 border border-[#EBEBEC] shadow-none pl-3 pr-0 data-[hover=true]:bg-zinc-200/60",
  value:
    "text-sm font-normal leading-5 text-zinc-500 group-data-[has-value=true]:text-zinc-900",
  selectorIcon: "text-zinc-500 w-4 h-4 mr-3",
  popoverContent: "rounded-xl font-['Plus_Jakarta_Sans']",
};

// Desktop select widths from Figma; they shrink on narrower screens instead of overflowing.
const DESKTOP_SELECT = "w-[200px] shrink min-w-[120px]";
const selectWidth = (width: string) => ({ ...selectClassNames, base: width });

const mobileSelectClassNames = {
  base: "w-full",
  trigger:
    "h-10 min-h-10 rounded-xl bg-white border border-gray-300 shadow-none pl-3 pr-0 data-[hover=true]:bg-gray-50",
  value: "text-sm font-normal leading-5 text-gray-500 group-data-[has-value=true]:text-gray-900",
  selectorIcon: "text-gray-500 w-4 h-4 mr-3",
  popoverContent: "rounded-xl font-['Plus_Jakarta_Sans']",
};

const MarketplaceCardsFilters: React.FC<MarketplaceCardsFiltersProps> = ({
  profileType,
  activeTab,
  searchName,
  setSearchName,
  filterCardType,
  setFilterCardType,
  filterLocation,
  setFilterLocation,
  filterSport,
  setFilterSport,
  filterWeightClass,
  setFilterWeightClass,
  filterPurse,
  setFilterPurse,
  filterCurrency,
  setFilterCurrency,
  filterOpportunityType,
  setFilterOpportunityType,
  selectedRosterStatus,
  setSelectedRosterStatus,
  selectedTitle,
  setSelectedTitle,
  locations,
  sports,
  weightClasses,
  purses,
  opportunityTypes,
  rosterStatus,
  handleResetFilters,
  setIsCreateRosterModalOpen,
  setIsOpportunityModalOpen,
  connectStatus,
  connectStatusLoading,
  onCreateOpportunity,
  onTabChange,
}) => {
  const { t } = useTranslation("marketPlaceCards");
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  const formatOption = (item: string) =>
    item
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  const titleOptions = ["All Titles", "Yes", "No"];

  const handleRazorpayConnect = async () => {
    try {
      const url = await connectStripe();
      if (url) {
        window.open(url, "_blank");
      }
    } catch (error: any) {
      console.error("Razorpay connect failed:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          t("failed_to_load_account_details")
      );
    }
  };

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filterCardType) count++;
    if (filterLocation) count++;
    if (filterSport) count++;
    if (filterWeightClass) count++;
    if (filterPurse) count++;
    if (selectedTitle && selectedTitle !== "All Titles") count++;
    if (selectedRosterStatus) count++;
    if (filterOpportunityType) count++;
    return count;
  }, [
    filterCardType,
    filterLocation,
    filterSport,
    filterWeightClass,
    filterPurse,
    selectedTitle,
    selectedRosterStatus,
    filterOpportunityType,
  ]);

  const actionButtonClass = "h-8 px-3 rounded-2xl text-sm font-medium";
  const primaryButtonClass = `${actionButtonClass} bg-[#0485F7] text-white`;

  return (
    <>
      <div className="lg:hidden flex items-center justify-between gap-2 px-1 pt-1 pb-3 font-['Plus_Jakarta_Sans']">
        <div className="flex items-center bg-gray-100 p-1 rounded-full shrink-0">
          <button
            type="button"
            onClick={() => onTabChange("all")}
            className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
              activeTab === "all"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            {t("all_cards")}
          </button>
          <button
            type="button"
            onClick={() => onTabChange("myCards")}
            className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
              activeTab === "myCards"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            {t("my_cards")}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
          {isSearchOpen && (
            <div className="flex-1 min-w-0">
              <Input
                autoFocus
                aria-label={t("search_by_name")}
                placeholder={t("search_by_name")}
                value={searchName}
                onChange={(event) => setSearchName(event.target.value)}
                startContent={
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                }
                size="sm"
                radius="full"
                className="w-full"
                classNames={{
                  inputWrapper:
                    "h-9 bg-white border border-gray-200 shadow-none rounded-full",
                  input:
                    "text-xs sm:text-sm font-normal leading-5 placeholder:text-gray-400",
                }}
              />
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsSearchOpen((prev) => !prev)}
            className={`size-9 min-w-9 rounded-2xl border transition-colors flex items-center justify-center shrink-0 shadow-sm ${
              isSearchOpen
                ? "bg-blue-50 border-blue-200 text-blue-600"
                : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            }`}
            aria-label="Search"
          >
            <MagnifyingGlassIcon className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => setIsFilterDrawerOpen(true)}
            className="size-9 min-w-9 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-50 transition-colors shadow-sm relative shrink-0"
            aria-label="Filter"
          >
            <FunnelIcon className="w-4 h-4 text-gray-700" />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 size-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          {activeTab === "myCards" && !connectStatusLoading && connectStatus !== null && (
            <>
              {(profileType === "MANAGER" || profileType === "ORGANIZATION") && (
                <>
                  {connectStatus === "ACCOUNT_ACTIVE" ? (
                    <button
                      type="button"
                      onClick={onCreateOpportunity}
                      className="size-9 min-w-9 rounded-2xl bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors shadow-sm shrink-0"
                      aria-label={t("create_opportunity")}
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  ) : connectStatus !== "VERIFICATION_PENDING" ? (
                    <button
                      type="button"
                      onClick={handleRazorpayConnect}
                      disabled={connectStatusLoading}
                      className="size-9 min-w-9 rounded-2xl bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors shadow-sm shrink-0"
                      aria-label={t("add_account")}
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  ) : null}
                </>
              )}

              {profileType === "FIGHTER" && (
                <>
                  {connectStatus === "ACCOUNT_ACTIVE" ? (
                    <button
                      type="button"
                      onClick={() => setIsCreateRosterModalOpen(true)}
                      className="size-9 min-w-9 rounded-2xl bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors shadow-sm shrink-0"
                      aria-label={t("create_card")}
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  ) : connectStatus !== "VERIFICATION_PENDING" ? (
                    <button
                      type="button"
                      onClick={handleRazorpayConnect}
                      disabled={connectStatusLoading}
                      className="size-9 min-w-9 rounded-2xl bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors shadow-sm shrink-0"
                      aria-label={t("add_account")}
                    >
                      <PlusIcon className="w-5 h-5" />
                    </button>
                  ) : null}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {isFilterDrawerOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center lg:hidden">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
            onClick={() => setIsFilterDrawerOpen(false)}
          />
          <div className="relative w-full max-h-[85vh] rounded-t-3xl bg-white p-5 sm:p-6 shadow-2xl flex flex-col z-10 font-['Plus_Jakarta_Sans']">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">
                  {t("filters", { defaultValue: "Filters" })}
                </h2>
                {activeFilterCount > 0 && (
                  <span className="size-5 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                className="size-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
                aria-label="Close filters"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto flex flex-col gap-3.5 py-4 min-h-0">
              {(profileType === "MANAGER" ||
                profileType === "ORGANIZATION" ||
                profileType === "FIGHTER") && (
                <>
                  <AppSelect
                    aria-label={t("select_type")}
                    placeholder={t("select_type")}
                    selectedKeys={filterCardType ? [filterCardType] : []}
                    onSelectionChange={(key) =>
                      setFilterCardType(Array.from(key)[0] as string)
                    }
                    classNames={mobileSelectClassNames}
                    size="sm"
                  >
                    <SelectItem key="MANAGER">{t("manager")}</SelectItem>
                    <SelectItem key="FIGHTER">{t("athlete")}</SelectItem>
                    <SelectItem key="ORGANIZATION">{t("organization")}</SelectItem>
                  </AppSelect>
                  <AppSelect
                    aria-label={t("select_location")}
                    placeholder={t("select_location")}
                    selectedKeys={filterLocation ? [filterLocation] : []}
                    onSelectionChange={(key) =>
                      setFilterLocation(Array.from(key)[0] as string)
                    }
                    classNames={mobileSelectClassNames}
                    size="sm"
                  >
                    {locations.map((loc) => (
                      <SelectItem key={loc}>{formatOption(loc)}</SelectItem>
                    ))}
                  </AppSelect>
                </>
              )}

              {profileType === "FIGHTER" && (
                <>
                  <AppSelect
                    aria-label={t("select_sport")}
                    placeholder={t("select_sport")}
                    selectedKeys={filterSport ? [filterSport] : []}
                    onSelectionChange={(key) =>
                      setFilterSport(Array.from(key)[0] as string)
                    }
                    classNames={mobileSelectClassNames}
                    size="sm"
                  >
                    {sports.map((sport) => (
                      <SelectItem key={sport}>{formatOption(sport)}</SelectItem>
                    ))}
                  </AppSelect>
                  <AppSelect
                    aria-label={t("select_weight_class")}
                    placeholder={t("select_weight_class")}
                    selectedKeys={filterWeightClass ? [filterWeightClass] : []}
                    onSelectionChange={(key) =>
                      setFilterWeightClass(Array.from(key)[0] as string)
                    }
                    classNames={mobileSelectClassNames}
                    size="sm"
                  >
                    {weightClasses.map((wc) => (
                      <SelectItem key={wc}>{formatOption(wc)}</SelectItem>
                    ))}
                  </AppSelect>
                  <AppSelect
                    aria-label={t("select_purse")}
                    placeholder={t("select_purse")}
                    selectedKeys={filterPurse ? [filterPurse] : []}
                    onSelectionChange={(key) =>
                      setFilterPurse(Array.from(key)[0] as string)
                    }
                    classNames={mobileSelectClassNames}
                    size="sm"
                  >
                    {purses.map((purse) => (
                      <SelectItem key={purse}>{formatOption(purse)}</SelectItem>
                    ))}
                  </AppSelect>
                  <AppSelect
                    aria-label={t("title_holder_placeholder")}
                    placeholder={t("title_holder_placeholder")}
                    selectedKeys={selectedTitle ? [selectedTitle] : []}
                    onSelectionChange={(keys) =>
                      setSelectedTitle((Array.from(keys)[0] as string) || "")
                    }
                    classNames={mobileSelectClassNames}
                    size="sm"
                  >
                    {titleOptions.map((option) => (
                      <SelectItem key={option}>{option}</SelectItem>
                    ))}
                  </AppSelect>
                  <AppSelect
                    aria-label={t("select_roster_status")}
                    placeholder={t("select_roster_status")}
                    selectedKeys={selectedRosterStatus ? [selectedRosterStatus] : []}
                    onSelectionChange={(key) =>
                      setSelectedRosterStatus(Array.from(key)[0] as string)
                    }
                    classNames={mobileSelectClassNames}
                    size="sm"
                  >
                    {rosterStatus.map((status) => (
                      <SelectItem key={status}>{formatOption(status)}</SelectItem>
                    ))}
                  </AppSelect>
                </>
              )}

              {(profileType === "MANAGER" ||
                profileType === "ORGANIZATION") && (
                <>
                  <AppSelect
                    aria-label={t("select_opportunity_type")}
                    placeholder={t("select_opportunity_type")}
                    selectedKeys={
                      filterOpportunityType ? [filterOpportunityType] : []
                    }
                    onSelectionChange={(key) =>
                      setFilterOpportunityType(Array.from(key)[0] as string)
                    }
                    classNames={mobileSelectClassNames}
                    size="sm"
                  >
                    {opportunityTypes.map((opt) => (
                      <SelectItem key={opt}>{formatOption(opt)}</SelectItem>
                    ))}
                  </AppSelect>
                </>
              )}

              {activeTab === "myCards" && !connectStatusLoading && connectStatus !== null && (
                <div className="pt-2 w-full [&>button]:w-full [&>button]:h-10 [&>button]:rounded-xl">
                  <StripeAccountDetailsModal />
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-3 border-t border-gray-100 shrink-0">
              <button
                type="button"
                onClick={() => {
                  handleResetFilters();
                  setIsFilterDrawerOpen(false);
                }}
                className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors text-center"
              >
                {t("reset_filters", { defaultValue: "Reset all" })}
              </button>
              <button
                type="button"
                onClick={() => setIsFilterDrawerOpen(false)}
                className="flex-1 py-2.5 px-4 rounded-xl bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 transition-colors text-center shadow-sm"
              >
                {t("apply_filters", { defaultValue: "Apply Filters" })}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="hidden lg:flex flex-wrap items-center justify-between gap-4 w-full font-['Plus_Jakarta_Sans']">
        <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans'] shrink-0">Marketplace cards</h1>

        <div className="flex flex-wrap items-center gap-2">

        <AppSelect
          aria-label={t("all_cards")}
          disallowEmptySelection
          selectedKeys={[activeTab]}
          onSelectionChange={(key) => {
            const next = Array.from(key)[0];
            if (next) onTabChange(next as string);
          }}
          classNames={selectWidth(DESKTOP_SELECT)}
          size="sm"
        >
          <SelectItem key="all">{t("all_cards")}</SelectItem>
          <SelectItem key="myCards">{t("my_cards")}</SelectItem>
        </AppSelect>

        {profileType === "FIGHTER" && (
          <>
            <AppSelect
              aria-label={t("select_purse")}
              placeholder={t("select_purse")}
              selectedKeys={filterPurse ? [filterPurse] : []}
              onSelectionChange={(key) =>
                setFilterPurse(Array.from(key)[0] as string)
              }
              classNames={selectWidth(DESKTOP_SELECT)}
              size="sm"
            >
              {purses.map((purse) => (
                <SelectItem key={purse}>{formatOption(purse)}</SelectItem>
              ))}
            </AppSelect>
            <AppSelect
              aria-label={t("select_sport")}
              placeholder={t("select_sport")}
              selectedKeys={filterSport ? [filterSport] : []}
              onSelectionChange={(key) =>
                setFilterSport(Array.from(key)[0] as string)
              }
              classNames={selectWidth(DESKTOP_SELECT)}
              size="sm"
            >
              {sports.map((sport) => (
                <SelectItem key={sport}>{formatOption(sport)}</SelectItem>
              ))}
            </AppSelect>
            <AppSelect
              aria-label={t("select_weight_class")}
              placeholder={t("select_weight_class")}
              selectedKeys={filterWeightClass ? [filterWeightClass] : []}
              onSelectionChange={(key) =>
                setFilterWeightClass(Array.from(key)[0] as string)
              }
              classNames={{
                ...selectWidth("w-[160px] shrink min-w-[110px]"),
                value: `${selectClassNames.value} truncate`,
              }}
              size="sm"
            >
              {weightClasses.map((wc) => (
                <SelectItem key={wc}>{formatOption(wc)}</SelectItem>
              ))}
            </AppSelect>
          </>
        )}

        {(profileType === "MANAGER" ||
          profileType === "ORGANIZATION" ||
          profileType === "FIGHTER") && (
          <AppSelect
            aria-label={t("select_location")}
            placeholder={t("select_location")}
            selectedKeys={filterLocation ? [filterLocation] : []}
            onSelectionChange={(key) =>
              setFilterLocation(Array.from(key)[0] as string)
            }
            classNames={selectWidth("w-[256px] shrink min-w-[130px]")}
            size="sm"
          >
            {locations.map((loc) => (
              <SelectItem key={loc}>{formatOption(loc)}</SelectItem>
            ))}
          </AppSelect>
        )}

        <Tooltip content={t("reset_filters")}>
          <Button
            isIconOnly
            variant="light"
            onPress={handleResetFilters}
            className="h-9 w-9 min-w-9 rounded-xl text-zinc-600 hover:text-zinc-800"
            size="sm"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </Button>
        </Tooltip>

        {activeTab === "myCards" && !connectStatusLoading && connectStatus !== null && (
          <div className="flex gap-2">
            {(profileType === "MANAGER" || profileType === "ORGANIZATION") && (
              <>
                {connectStatus === "VERIFICATION_PENDING" ? (
                  <div className="flex items-center gap-2">
                    <Button
                      color="primary"
                      className={actionButtonClass}
                      size="sm"
                      isDisabled
                    >
                      {t("verification_pending")}
                    </Button>
                    <StripeAccountDetailsModal />
                  </div>
                ) : connectStatus !== "ACCOUNT_ACTIVE" ? (
                  <div className="flex items-center gap-1">
                    <Tooltip content="A connected Stripe account is required to create opportunities.">
                      <InformationCircleIcon className="w-5 h-5 text-gray-400 cursor-pointer" />
                    </Tooltip>
                    <Button
                      color="secondary"
                      startContent={<PlusIcon className="w-4 h-4" />}
                      className={actionButtonClass}
                      size="sm"
                      onPress={handleRazorpayConnect}
                      isLoading={connectStatusLoading}
                      isDisabled={connectStatusLoading}
                    >
                      {t("add_account")}
                    </Button>
                  </div>
                ) : null}
              </>
            )}

            {profileType === "FIGHTER" &&
              connectStatus !== "ACCOUNT_ACTIVE" &&
              connectStatus !== "VERIFICATION_PENDING" && (
                <div className="flex items-center gap-1">
                  <Tooltip content="A connected account is required to create your roster card.">
                    <InformationCircleIcon className="w-5 h-5 text-gray-400 cursor-pointer" />
                  </Tooltip>
                  <Button
                    color="secondary"
                    startContent={<PlusIcon className="w-4 h-4" />}
                    className={actionButtonClass}
                    size="sm"
                    onPress={handleRazorpayConnect}
                    isLoading={connectStatusLoading}
                    isDisabled={connectStatusLoading}
                  >
                    {t("add_account")}
                  </Button>
                </div>
              )}

            {profileType === "FIGHTER" &&
              connectStatus === "VERIFICATION_PENDING" && (
                <div className="flex items-center gap-2">
                  <Button
                    color="primary"
                    className={actionButtonClass}
                    size="sm"
                    isDisabled
                  >
                    {t("verification_pending")}
                  </Button>
                  <StripeAccountDetailsModal />
                </div>
              )}

            {profileType === "FIGHTER" && connectStatus === "ACCOUNT_ACTIVE" && (
              <div className="flex items-center gap-2">
                <Button
                  color="primary"
                  startContent={<PlusIcon className="w-4 h-4" />}
                  className={primaryButtonClass}
                  size="sm"
                  onPress={() => setIsCreateRosterModalOpen(true)}
                >
                  {t("create_card")}
                </Button>
                <StripeAccountDetailsModal />
              </div>
            )}

            {(profileType === "MANAGER" || profileType === "ORGANIZATION") &&
              connectStatus === "ACCOUNT_ACTIVE" && (
                <div className="flex items-center gap-2">
                  <Button
                    color="primary"
                    startContent={<PlusIcon className="w-4 h-4" />}
                    className={primaryButtonClass}
                    size="sm"
                    onPress={onCreateOpportunity}
                  >
                    {t("create_opportunity")}
                  </Button>
                  <StripeAccountDetailsModal />
                </div>
              )}
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default MarketplaceCardsFilters;
